const PREFIX = 'equine-perf:';

const MARKS = [
  'auth-script-start',
  'dom-content-loaded',
  'app-init-start',
  'app-config-ready',
  'extensions-load-start',
  'extensions-load-end',
  'extensions-register-start',
  'extensions-register-end',
  'modes-load-start',
  'modes-load-end',
  'mode-factories-end',
  'app-init-complete',
  'viewing-session-start',
  'viewing-session-end',
  // Mirrors the inline list in platform/app/public/inject-auth.js. The two must
  // stay in step: inject-auth.js is what actually prints the [equine-perf] line.
  'first-qido-request',
  'first-qido-response',
  'first-metadata-request',
  'first-metadata-response',
  'first-frame-request',
  'first-frame-response',
];

function markName(name) {
  return `${PREFIX}${name}`;
}

function firstEntry(performance, name) {
  if (!performance || typeof performance.getEntriesByName !== 'function') return null;
  return performance.getEntriesByName(markName(name))[0] || null;
}

function markTime(performance, name) {
  const entry = firstEntry(performance, name);
  return entry ? Math.round(entry.startTime * 100) / 100 : null;
}

function duration(performance, start, end) {
  const startTime = markTime(performance, start);
  const endTime = markTime(performance, end);
  return startTime === null || endTime === null
    ? null
    : Math.round((endTime - startTime) * 100) / 100;
}

export function createEquineStartupPerformance({
  enabled = false,
  performance = globalThis.performance,
  console: logger = globalThis.console,
} = {}) {
  const mark = (name, detail) => {
    if (!enabled || !performance || typeof performance.mark !== 'function') return;
    try {
      performance.mark(markName(name), detail === undefined ? undefined : { detail });
    } catch (_) {
      // Older browsers may reject mark options; timing must never affect startup.
      performance.mark(markName(name));
    }
  };

  const report = trigger => {
    if (!enabled || !performance) return null;
    const marks = Object.fromEntries(
      MARKS.map(name => [name, markTime(performance, name)]).filter(([, value]) => value !== null)
    );
    const result = {
      trigger,
      marks,
      navigation: {
        firstMetadataAfterNavigationMs: markTime(performance, 'first-metadata-request'),
        firstFrameAfterNavigationMs: markTime(performance, 'first-frame-request'),
      },
      durations: {
        appInit: duration(performance, 'app-init-start', 'app-init-complete'),
        config: duration(performance, 'app-init-start', 'app-config-ready'),
        extensions: duration(performance, 'extensions-load-start', 'extensions-load-end'),
        extensionsRegister: duration(
          performance,
          'extensions-register-start',
          'extensions-register-end'
        ),
        modes: duration(performance, 'modes-load-start', 'modes-load-end'),
        modeFactories: duration(performance, 'modes-load-end', 'mode-factories-end'),
        viewingSession: duration(performance, 'viewing-session-start', 'viewing-session-end'),
        metadataToFirstFrame: duration(
          performance,
          'first-metadata-request',
          'first-frame-request'
        ),
      },
    };
    result.navigation.warmTargetMs = 2000;
    result.navigation.firstMetadataUnderWarmTarget =
      result.navigation.firstMetadataAfterNavigationMs !== null &&
      result.navigation.firstMetadataAfterNavigationMs < result.navigation.warmTargetMs;
    if (typeof logger?.info === 'function') logger.info('[equine-perf]', JSON.stringify(result));
    return result;
  };

  return { mark, report };
}
